// src/components/auth/LoginScreen.tsx
import React, { useState } from 'react';
import './LoginScreen.css';

import { VERSION_INFO } from '../../constants/version';

interface LoginScreenProps {
  onLogin: (userData: { name: string, type: 'token' | 'userpass' }, aiStatus?: any) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  // Main login type state
  const [showPopup, setShowPopup] = useState<'none' | 'token' | 'userpass' | 'admin'>('none');

  // Form states
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // REAL API AUTHENTICATION
  const handleTokenLogin = async () => {
    if (token.trim().length < 10) return;
    setIsLoading(true);

    try {
      const apiBaseUrl = process.env.REACT_APP_USER_MGMT_URL || 'http://localhost:8012';

      const response = await fetch(`${apiBaseUrl}/auth/login/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: token.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem('auth_token', data.access_token);
        sessionStorage.setItem('session_id', data.session_id);
        onLogin(data.user_info, data.ai_status);
        closePopup();
      } else {
        const errorData = await response.text();
        console.error('Login failed:', response.status, errorData);
        alert('Login failed. Please check your token.');
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserPassLogin = async () => {
    if (!username.trim() || !password.trim()) return;
    setIsLoading(true);

    try {
      const apiBaseUrl = process.env.REACT_APP_USER_MGMT_URL || 'http://localhost:8012';

      const response = await fetch(`${apiBaseUrl}/auth/login/userpass`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: username.trim(), password: password.trim() }),
      });

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem('auth_token', data.access_token);
        sessionStorage.setItem('session_id', data.session_id);
        onLogin(data.user_info, data.ai_status);
        closePopup();
      } else {
        alert('Login failed. Please check your credentials.');
      }
    } catch (error) {
      alert('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminAccess = () => {
    // Simple admin password check - in real implementation, this would be more secure
    if (adminPassword === 'admin123') {
      // Create temporary admin session
      const adminUser = {
        name: 'System Administrator',
        type: 'userpass' as const
      };
      sessionStorage.setItem('admin_access', 'true');
      onLogin(adminUser);
    } else {
      alert('Invalid admin password.');
    }
    setAdminPassword('');
  };

  const closePopup = () => {
    setShowPopup('none');
    setToken('');
    setUsername('');
    setPassword('');
    setAdminPassword('');
    setIsLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <div className="login-screen">
      <div className="login-content">

        {/* Animated Testwerk.ai Title */}
        <div className="center-title">
          <div className="logo-container">
            <h1 className="logo-main">
              <span className="red-text">T</span> E S T <span className="red-text">W</span> E R K
            </h1>
          </div>
          <h2 className="subtitle">AI STUDIO</h2>
        </div>

        <div className="login-selector">
          <h3 className="login-heading">LOGIN</h3>
          <div className="login-buttons-row">
            <button
              className="pushable push-primary1 btn-sm"
              onClick={() => setShowPopup('token')}
            >
              <span className="shadow"></span>
              <span className="edge"></span>
              <span className="front">TOKEN</span>
            </button>

            <button
              className="pushable push-primary1 btn-sm"
              onClick={() => setShowPopup('userpass')}
            >
              <span className="shadow"></span>
              <span className="edge"></span>
              <span className="front">USER LOGIN</span>
            </button>
          </div>
        </div>

      </div>


      {/* Token Login Popup */}
      {showPopup === 'token' && (
        <div className="login-popup-overlay" onClick={closePopup}>
          <div className="login-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h4>Token Access</h4>
              <button className="close-btn" onClick={closePopup}>×</button>
            </div>
            <div className="popup-content">
              <input
                type="text"
                placeholder="Enter your access token..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleTokenLogin)}
                className="login-input"
                autoFocus
              />
              <div className="popup-buttons">
                <button
                  className="pushable push-primary btn-sm"
                  onClick={handleTokenLogin}
                  disabled={isLoading || token.trim().length < 10}
                >
                  <span className="shadow"></span>
                  <span className="edge"></span>
                  <span className="front">
                    {isLoading ? 'Checking...' : 'Login'}
                  </span>
                </button>
                <button
                  className="pushable push-secondary btn-sm"
                  onClick={closePopup}
                >
                  <span className="shadow"></span>
                  <span className="edge"></span>
                  <span className="front">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User/Pass Login Popup */}
      {showPopup === 'userpass' && (
        <div className="login-popup-overlay" onClick={closePopup}>
          <div className="login-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h4>User Login</h4>
              <button className="close-btn" onClick={closePopup}>×</button>
            </div>
            <div className="popup-content">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="login-input"
                autoFocus
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleUserPassLogin)}
                className="login-input"
              />
              <div className="popup-buttons">
                <button
                  className="pushable push-primary btn-sm"
                  onClick={handleUserPassLogin}
                  disabled={isLoading || !username.trim() || !password.trim()}
                >
                  <span className="shadow"></span>
                  <span className="edge"></span>
                  <span className="front">
                    {isLoading ? 'Logging in...' : 'Login'}
                  </span>
                </button>
                <button
                  className="pushable push-secondary btn-sm"
                  onClick={closePopup}
                >
                  <span className="shadow"></span>
                  <span className="edge"></span>
                  <span className="front">Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default LoginScreen;